/**
 * TanStack Query hooks for the current user's in-app notification feed.
 *
 * Poll interval is conservative (30s) — the user almost always lands on a
 * page that triggers a fresh fetch, so polling is the safety net for the
 * cases where they're on a long-lived view (e.g. dispatch board).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification, NotificationEvent } from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';

const KEY = ['me', 'notifications'] as const;

export interface NotificationsResponse {
  data: Notification[];
  unread_count: number;
}

/** Payload shape the dispatcher persists — used by the bell dropdown. */
export interface NotificationStoredPayload {
  event: NotificationEvent;
  channel: string;
  subject: string;
  short: string;
  body: string;
  action_url: string | null;
  context: Record<string, unknown>;
}

export function useMyNotifications(opts?: { unread?: boolean }) {
  const qs = opts?.unread ? '?unread=true' : '';
  return useQuery<NotificationsResponse>({
    queryKey: [...KEY, opts?.unread ?? false],
    queryFn: () =>
      apiCall<NotificationsResponse>(`/me/notifications${qs}`),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useUnreadCount() {
  return useQuery<{ unread_count: number }>({
    queryKey: [...KEY, 'unread-count'],
    queryFn: () =>
      apiCall<{ unread_count: number }>('/me/notifications/unread-count'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiCall<void>(`/me/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation<{ acknowledged: number }, Error, void>({
    mutationFn: () =>
      apiCall<{ acknowledged: number }>(`/me/notifications/read-all`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
